import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useBranding from "@/hooks/useBranding";
import { GraduationCap, MapPin } from "lucide-react";

export default function PublicFooter() {
  const b = useBranding();
  const [cities, setCities] = useState([]);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => setCities([]));
    api.get("/pages").then((r) => setPages(r.data || [])).catch(() => setPages([]));
  }, []);

  // Ensure we always show a decent grid: 3 columns × at least 7 rows = 21 cities.
  // If the DB doesn't have that many, top up with common Indian cities as fallback.
  const fallback = [
    "Delhi", "Noida", "Gurgaon", "Ghaziabad", "Faridabad", "Laxmi Nagar",
    "Mumbai", "Pune", "Thane", "Navi Mumbai",
    "Bangalore", "Chennai", "Hyderabad", "Kolkata",
    "Jaipur", "Lucknow", "Kanpur", "Chandigarh",
    "Ahmedabad", "Surat", "Indore", "Bhopal",
    "Patna", "Ranchi", "Bhubaneswar", "Guwahati",
  ];
  const seen = new Set(cities.map((c) => c.toLowerCase()));
  const merged = [...cities];
  for (const c of fallback) {
    if (!seen.has(c.toLowerCase())) {
      merged.push(c);
      seen.add(c.toLowerCase());
    }
  }
  const displayCities = merged.slice(0, Math.max(21, merged.length));

  const footerPages = pages.filter((p) => p.show_in_footer !== false);

  const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  return (
    <footer className="mt-16 bg-slate-900 text-slate-300" data-testid="public-footer">
      {/* City links: 3 columns, at least 7 rows */}
      <div className="border-b border-slate-800">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="flex items-center gap-2 mb-6 text-amber-400">
            <MapPin className="w-4 h-4" />
            <h3 className="text-sm font-bold tracking-widest uppercase">Explore Schools by City</h3>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 text-sm"
            data-testid="footer-city-grid"
          >
            {displayCities.map((c) => (
              <Link
                key={c}
                to={`/schools/in-${slugify(c)}`}
                className="text-slate-400 hover:text-amber-400 transition-colors py-1 border-b border-slate-800/60"
                data-testid={`footer-city-${slugify(c)}`}
              >
                School in {c}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Brand + editorial links */}
      <div className="mx-auto max-w-7xl px-6 py-10 grid gap-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            {b.site_logo_url ? (
              <img
                src={b.site_logo_url}
                alt={b.site_title || "School Dial"}
                className="h-9 w-auto max-w-[180px] object-contain bg-white/5 rounded p-1"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <>
                <div className="w-9 h-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-amber-400" />
                </div>
                <span className="font-black text-white text-lg tracking-tight">{b.site_title || "School Dial"}</span>
              </>
            )}
          </div>
          <p className="text-sm text-slate-400 max-w-md leading-relaxed">
            {b.site_description ||
              "India's most trusted school directory. Discover schools, compare, and connect with admissions teams — all in one place."}
          </p>
        </div>
        <div>
          <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-3">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/schools" className="hover:text-amber-400" data-testid="footer-link-schools">Browse Schools</Link></li>
            <li><Link to="/blogs" className="hover:text-amber-400" data-testid="footer-link-blogs">Blog</Link></li>
            <li><Link to="/events" className="hover:text-amber-400" data-testid="footer-link-events">Events</Link></li>
            <li><Link to="/list-your-school" className="hover:text-amber-400" data-testid="footer-link-list">List Your School</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-3">Company</h4>
          <ul className="space-y-2 text-sm">
            {footerPages.length === 0 && (
              <>
                <li><Link to="/pages/about-us" className="hover:text-amber-400" data-testid="footer-link-about-us">About Us</Link></li>
                <li><Link to="/pages/contact-us" className="hover:text-amber-400" data-testid="footer-link-contact-us">Contact Us</Link></li>
                <li><Link to="/pages/privacy-policy" className="hover:text-amber-400" data-testid="footer-link-privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/pages/terms-of-service" className="hover:text-amber-400" data-testid="footer-link-terms-of-service">Terms of Service</Link></li>
              </>
            )}
            {footerPages.map((p) => (
              <li key={p.slug}>
                <Link to={`/pages/${p.slug}`} className="hover:text-amber-400" data-testid={`footer-link-${p.slug}`}>{p.title}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} {b.site_title || "School Dial"}. Built for parents & educators.
      </div>
    </footer>
  );
}
